import { Alert, Button, Drawer, Modal, ResizeBox, Spin, Tabs } from "@arco-design/web-react";
import {
  ArrowLeft,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronUp,
  CloudAlert,
  CloudUpload,
  RefreshCw,
  Save,
} from "lucide-react";
import type { KeyboardEvent, ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { ITINERARY_SIDE_TABS } from "../config";
import type { ItinerarySaveStatus } from "../runtime";
import { requiresSchoolReturnConfirmation, resolveDayEndpointNodes, summarizeDayEndpoints } from "../service";
import type {
  DrivingRequest,
  DrivingResult,
  EnrichedContent,
  EnrichRequest,
  ItineraryDay,
  FlightQuery,
  FlightSearchResult,
  ItineraryEditor,
  ItineraryNode,
  ItineraryValidationIssue,
  MajorCandidate,
  MajorCandidateQuery,
  PointCandidateResult,
  PointCandidateQuery,
  PointDescriptionCompletionRequest,
  PointDescriptionCompletionResult,
  RailwayQuery,
  RailwaySearchResult,
  TimeSlot,
  WalkingRequest,
  WalkingResult,
} from "../types";
import { derivePublicItinerary } from "../service/itineraryOutputService";
import { composeDaySummary, pendingContentSlots, requestDaySummaryRewrite } from "../service/itinerarySummaryService";
import { ItineraryDialogCloseButton } from "./ItineraryDialogCloseButton";
import { ItineraryMainDataDialog } from "./ItineraryMainDataDialog";
import type { MainDataCreateRequest, MainDataKind, MainDataResult } from "../types/mainData";
import { ItineraryClientDetail } from "./ItineraryClientDetail";
import { ItineraryDaySidebar } from "./ItineraryDaySidebar";
import { ItineraryContentPanel } from "./ItineraryContentPanel";
import { ItineraryServiceStandards } from "./ItineraryServiceStandards";
import { ItineraryInternalActions } from "./ItineraryInternalActions";
import { ItinerarySkeletonPanel } from "./ItinerarySkeletonPanel";
import { ItineraryOutputDrawer } from "./ItineraryOutputDrawer";
import { ItinerarySidePreview } from "./ItinerarySidePreview";
import { ItineraryQuickPreview } from "./ItineraryQuickPreview";
import { ItineraryTablePanel } from "./ItineraryTablePanel";
import "./itineraryEditor.css";

type Props = {
  editor: ItineraryEditor;
  selectedDayId: string;
  saveStatus: ItinerarySaveStatus;
  validationIssues: ItineraryValidationIssue[];
  repairRequiredCount?: number;
  repairRemainingCount?: number;
  embed?: boolean;
  errorMessage?: string;
  errorDiagnostic?: string;
  onChange: (editor: ItineraryEditor) => void;
  onSelectDay: (dayId: string) => void;
  onSave: () => boolean | void | Promise<boolean | void>;
  onRetrySave: () => void | Promise<void>;
  onReloadLatest?: () => void | Promise<void>;
  onExit?: () => void;
  onRewritePendingSummaries?: () => Promise<void>;
  summaryRewriting?: boolean;
  contentSaveBlocked?: boolean;
  summaryRewriteWarning?: string;
  onEnsureSaved?: () => Promise<boolean>;
  onLoadOutputEditor?: () => Promise<ItineraryEditor>;
  savedRevision?: string;
  onEnrich?: (request: EnrichRequest, signal?: AbortSignal) => Promise<EnrichedContent>;
  onCompletePointDescriptions?: (
    request: PointDescriptionCompletionRequest,
    signal?: AbortSignal,
  ) => Promise<PointDescriptionCompletionResult>;
  onListPointCandidates?: (query: PointCandidateQuery, signal?: AbortSignal) => Promise<PointCandidateResult>;
  onListMajorCandidates?: (query?: MajorCandidateQuery) => Promise<MajorCandidate[]>;
  onGetRailwayOptions?: (query: RailwayQuery, signal?: AbortSignal) => Promise<RailwaySearchResult>;
  onGetFlightOptions?: (query: FlightQuery, signal?: AbortSignal) => Promise<FlightSearchResult>;
  onGetDrivingRoute?: (request: DrivingRequest, signal?: AbortSignal) => Promise<DrivingResult>;
  onGetWalkingRoute?: (request: WalkingRequest, signal?: AbortSignal) => Promise<WalkingResult>;
  calculatingSegmentIds?: ReadonlySet<string>;
  canUpdate?: boolean;
  canCreatePoint?: boolean;
  canCreateStayArea?: boolean;
  onCreateMainData?: (request: MainDataCreateRequest) => Promise<MainDataResult>;
  editorDisabled?: boolean;
  canPreview?: boolean;
  canViewTable?: boolean;
  canExport?: boolean;
  canShare?: boolean;
  enableInternalInteractions?: boolean;
  onLocalDraftChange?: (dirty: boolean) => void;
  copyActions?: ReactNode;
  moveActions?: ReactNode;
  onStartMove?: () => void;
  onCancelMove?: () => void;
  movePending?: boolean;
  lifecycleActions?: ReactNode;
  reviewActions?: ReactNode;
  outputNotice?: { type: "success" | "error"; text: string; action?: ReactNode };
  customizationPanel?: ReactNode;
  associationNotice?: ReactNode;
  legacySyncNotice?: ReactNode;
};

const statusViews: Record<ItinerarySaveStatus, { label: string; icon: typeof Save }> = {
  clean: { label: "已同步", icon: Check },
  dirty: { label: "正在保存本地草稿", icon: CloudUpload },
  local_saved: { label: "已自动保存到本地", icon: Check },
  saving: { label: "正在保存到服务器", icon: CloudUpload },
  saved: { label: "已保存到服务器", icon: Check },
  error: { label: "保存失败", icon: CloudAlert },
};

const itineraryStatusLabels: Record<string, string> = {
  draft: "草稿",
  active: "草稿",
  confirmed: "定稿",
  reviewing: "审稿",
  archived: "已归档",
};

type EditorView = "skeleton" | "content" | "services";

const editorViewOrder: EditorView[] = ["skeleton", "content", "services"];

const sidePreviewStorageKey = (itineraryId: string) => `flower-master:itinerary-side-preview:v1:${itineraryId}`;
type SidePanelTab = typeof ITINERARY_SIDE_TABS[number]["key"];

/** 读取当前行程的右栏选项；存储不可用时回退到定制信息。 */
function readSidePreviewPreference(itineraryId: string): SidePanelTab {
  try {
    const saved = globalThis.sessionStorage?.getItem(sidePreviewStorageKey(itineraryId));
    return ITINERARY_SIDE_TABS.find((tab) => tab.key === saved)?.key ?? "customization";
  } catch {
    return "customization";
  }
}

/** 保存当前行程的右栏选项，避免刷新页面后丢失用户正在核对的预览。 */
function writeSidePreviewPreference(itineraryId: string, tab: SidePanelTab): void {
  try {
    if (tab !== "customization") globalThis.sessionStorage?.setItem(sidePreviewStorageKey(itineraryId), tab);
    else globalThis.sessionStorage?.removeItem(sidePreviewStorageKey(itineraryId));
  } catch {
    // 隐私模式或存储配额异常时不影响右栏切换。
  }
}

const validationTimeSlotLabels: Record<TimeSlot, string> = {
  morning: "上午",
  afternoon: "下午",
  evening: "晚上",
};

type ValidationSubject = {
  day: ItineraryDay;
  node: ItineraryNode;
  previous?: ItineraryNode;
  following?: ItineraryNode;
};

/** 将稳定校验键还原为用户可识别的天次、时段和节点位置。 */
function resolveValidationSubject(
  editor: ItineraryEditor,
  subjectKey?: string,
): ValidationSubject | null {
  const [dayId, pathId, ...nodeIdParts] = subjectKey?.split(":") ?? [];
  const nodeId = nodeIdParts.join(":");
  if (!dayId || !pathId || !nodeId) return null;
  const day = editor.days.find((item) => item.id === dayId);
  const node = day?.nodes.find((item) => item.id === nodeId);
  if (!day || !node) return null;
  const nodeById = new Map(day.nodes.map((item) => [item.id, item]));
  const pathNodes = pathId === "linear"
    ? [...day.nodes].sort((left, right) => {
        const slotOrder = Object.keys(validationTimeSlotLabels) as TimeSlot[];
        return slotOrder.indexOf(left.timeSlot) - slotOrder.indexOf(right.timeSlot)
          || left.sortOrder - right.sortOrder;
      })
    : day.parallelTracks?.tracks
        .find((track) => track.id === pathId)
        ?.nodeIds.map((id) => nodeById.get(id))
        .filter((item): item is ItineraryNode => Boolean(item))
      ?? [];
  const index = pathNodes.findIndex((item) => item.id === node.id);
  return {
    day,
    node,
    previous: index > 0 ? pathNodes[index - 1] : undefined,
    following: index >= 0 ? pathNodes[index + 1] : undefined,
  };
}

/** 把内部校验码转换为无需研发知识即可处理的中文提示。 */
function formatValidationIssue(issue: ItineraryValidationIssue, editor: ItineraryEditor): string {
  const subject = resolveValidationSubject(editor, issue.subjectKey);
  const location = subject
    ? `第 ${subject.day.dayNumber} 天${validationTimeSlotLabels[subject.node.timeSlot]}的“${subject.node.title.trim() || "未命名节点"}”`
    : null;
  if (issue.code === "intermediate-node-must-be-point") {
    const between = subject?.previous && subject.following
      ? `位于“${subject.previous.title.trim() || "未命名节点"}”和“${subject.following.title.trim() || "未命名节点"}”之间，`
      : "";
    return location
      ? `${location}${between}这里不能放学校校区，请把校区放在当天的出发或结束位置，或改选考察点、火车站、机场或住宿。`
      : "学校校区只能放在当天的出发或结束位置，请调整顺序，或改选考察点、火车站、机场或住宿。";
  }
  if (issue.code === "node-resource-required") {
    return location
      ? `${location}还没有选好对应地点，请重新选择地点后再保存。`
      : "有一处地点还没有选好，请重新选择后再保存。";
  }
  const detail = issue.detail.trim();
  return /[\u3400-\u9fff]/u.test(detail)
    ? detail
    : "有一处地点或交通安排需要检查，请确认地点是否选对、先后顺序是否正确。";
}

/** 将每日住宿差异用于人工核对提醒，不参与保存校验。 */
function dayStayNoticeDetail(day: ItineraryDay): string | null {
  const stayNodes = resolveDayEndpointNodes(day).filter((node) => node.nodeType === "stay_area");
  if (!stayNodes.length && day.stayAreaName?.trim() === "无") return null;
  const dayStayKey = day.stayAreaId?.trim() || day.stayAreaName?.trim().toLocaleLowerCase() || "";
  const nodeStayKeys = stayNodes.map((node) => node.stayAreaId?.trim() || node.title.trim().toLocaleLowerCase());
  const mismatch = Boolean(dayStayKey || nodeStayKeys.length)
    && (nodeStayKeys.length === 0 || !dayStayKey || !nodeStayKeys.includes(dayStayKey));
  if (!mismatch) return null;
  return `第 ${day.dayNumber} 天填写的住宿是“${day.stayAreaName?.trim() || "未填写"}”，行程里放的住宿是“${stayNodes.map((node) => node.title.trim()).join("、") || "未添加"}”。请确认当天实际住哪里。`;
}

/** 原生行程编辑器工作区。 */
export function ItineraryEditorFeaturePage({
  editor,
  selectedDayId,
  saveStatus,
  validationIssues,
  repairRequiredCount = 0,
  repairRemainingCount = 0,
  embed = false,
  errorMessage,
  errorDiagnostic,
  onChange,
  onSelectDay,
  onSave,
  onRetrySave,
  onReloadLatest,
  onExit,
  onRewritePendingSummaries,
  summaryRewriting = false,
  contentSaveBlocked = false,
  summaryRewriteWarning = "",
  onLoadOutputEditor,
  savedRevision,
  onEnrich,
  onCompletePointDescriptions,
  onListPointCandidates,
  onListMajorCandidates,
  onGetRailwayOptions,
  onGetFlightOptions,
  onGetDrivingRoute,
  onGetWalkingRoute,
  calculatingSegmentIds,
  canUpdate = true,
  canCreatePoint = false,
  canCreateStayArea = false,
  onCreateMainData,
  editorDisabled: externallyDisabled = false,
  canPreview = true,
  canViewTable = true,
  canExport = true,
  canShare = true,
  enableInternalInteractions = false,
  onLocalDraftChange,
  copyActions,
  moveActions,
  onStartMove,
  onCancelMove,
  movePending,
  lifecycleActions,
  reviewActions,
  outputNotice,
  customizationPanel,
  associationNotice,
  legacySyncNotice,
}: Props) {
  const hasCustomizationPanel = Boolean(customizationPanel);
  const [pendingReloadLatest, setPendingReloadLatest] = useState(false);
  const [reloadPending, setReloadPending] = useState(false);
  const editorDisabled = externallyDisabled || reloadPending;
  const [announcement, setAnnouncement] = useState("");
  const [announcementsState, setAnnouncementsState] = useState<"initial" | "expanded" | "collapsed">("initial");
  const announcementsCollapsed = announcementsState === "collapsed";
  const [allValidationVisible, setAllValidationVisible] = useState(false);
  const [allStayNoticesVisible, setAllStayNoticesVisible] = useState(false);
  const [daysOverlayMode, setDaysOverlayMode] = useState(
    () => globalThis.matchMedia?.("(max-width: 768px)").matches ?? false,
  );
  const [daysOpen, setDaysOpen] = useState(false);
  const [daysSidebarCollapsed, setDaysSidebarCollapsed] = useState(false);
  const [daysPanelSize, setDaysPanelSize] = useState(0.2);
  const daysPanelInteractedRef = useRef(false);
  const daysSplitRef = useCallback((element: HTMLDivElement | null) => {
    if (!element) return;
    daysPanelInteractedRef.current = false;
    const resize = () => {
      const width = element.getBoundingClientRect().width;
      if (width > 0 && !daysPanelInteractedRef.current) {
        // 外层分栏仍会改变实际宽度，按当前容器换算，避免一次性像素换算留下错误比例。
        setDaysPanelSize(Math.min(0.3, Math.max(0.05, 239 / width)));
      }
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const daysSidebarRef = useCallback((element: HTMLElement | null) => {
    if (!element) return;
    const resize = () => {
      const width = element.getBoundingClientRect().width;
      if (width > 0) setDaysSidebarCollapsed(width < 180);
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(element);
    // 绑定跟随真实节点的挂载/卸载，不保留嵌套分栏重建前的观察目标。
    return () => observer.disconnect();
  }, []);
  const [customizationPanelOpen, setCustomizationPanelOpen] = useState(true);
  const [customizationPanelSize, setCustomizationPanelSize] = useState<string>();
  const customizationPanelSizeRef = useRef<string | undefined>(undefined);
  const expandedCustomizationPanelSizeRef = useRef<string | undefined>(undefined);
  const [quickPreviewItineraryId, setQuickPreviewItineraryId] = useState<string>();
  useEffect(() => {
    setCustomizationPanelOpen(true);
    setCustomizationPanelSize(undefined);
    customizationPanelSizeRef.current = undefined;
    expandedCustomizationPanelSizeRef.current = undefined;
    setQuickPreviewItineraryId(undefined);
    setDaysSidebarCollapsed(false);
    setAnnouncementsState("initial");
    setAllValidationVisible(false);
    setAllStayNoticesVisible(false);
  }, [editor.id]);
  const [stackedPanels, setStackedPanels] = useState(
    () => globalThis.matchMedia?.("(max-width: 1024px)").matches ?? false,
  );
  const [view, setView] = useState<EditorView>("skeleton");
  const [outputOpen, setOutputOpen] = useState(false);
  const [sidePanelTab, setSidePanelTab] = useState(() => readSidePreviewPreference(editor.id));
  const [sidePreviewRefreshSequence, setSidePreviewRefreshSequence] = useState(0);
  const [modalDraftDirty, setModalDraftDirty] = useState(false);
  const [mainDataKind, setMainDataKind] = useState<MainDataKind>();
  const [mainDataDirty, setMainDataDirty] = useState(false);
  const [manualSavePending, setManualSavePending] = useState(false);
  const [pendingSchoolReturnSave, setPendingSchoolReturnSave] = useState<{
    editorId: string;
    trigger?: EventTarget | null;
    retry: boolean;
  }>();
  const manualSavePendingRef = useRef(false);
  const editorRef = useRef(editor);
  editorRef.current = editor;
  const [saveSummaryWarning, setSaveSummaryWarning] = useState("");
  const editingEnabled = canUpdate && !editorDisabled;
  const quickPreviewOpen = quickPreviewItineraryId === editor.id && view === "services" && editingEnabled;
  const auxiliaryPanelOpen = quickPreviewOpen || (hasCustomizationPanel && customizationPanelOpen);
  const hasAuxiliaryPanel = hasCustomizationPanel || quickPreviewOpen;
  const defaultAuxiliaryPanelSize = stackedPanels ? "220px" : "560px";
  const restoredCollapsedPanel = !quickPreviewOpen && customizationPanelSize !== undefined
    && Number.parseFloat(customizationPanelSize) === 0;
  const repairActive = repairRemainingCount > 0;
  const selectedDay = editor.days.find((day) => day.id === selectedDayId) ?? editor.days[0];
  const daysTriggerRef = useRef<HTMLElement | null>(null);
  const restoreDaysFocusRef = useRef(false);
  const outputTriggerRef = useRef<HTMLElement | null>(null);
  const restoreOutputFocusRef = useRef(false);
  const displayedSaveStatus = manualSavePending ? "saving" : saveStatus;
  const visibleSaveStatus = repairActive && displayedSaveStatus === "clean"
    ? { label: "待人工修复", icon: CloudAlert, className: "repair" }
    : { ...statusViews[displayedSaveStatus], className: displayedSaveStatus };
  const StatusIcon = visibleSaveStatus.icon;

  const closeQuickPreview = useCallback(() => {
    setCustomizationPanelSize(customizationPanelSizeRef.current);
    setQuickPreviewItineraryId(undefined);
  }, []);

  const selectEditorView = (nextView: EditorView) => {
    if (nextView === view) return true;
    if (quickPreviewOpen) closeQuickPreview();
    setView(nextView);
    if (nextView === "content" && editingEnabled && !manualSavePendingRef.current) {
      void (onRewritePendingSummaries ? onRewritePendingSummaries() : generateDraftContent());
    }
    return true;
  };

  const changeSidePanelTab = useCallback((tab: SidePanelTab) => {
    setSidePanelTab(tab);
    writeSidePreviewPreference(editor.id, tab);
  }, [editor.id]);

  useEffect(() => {
    const saved = readSidePreviewPreference(editor.id);
    changeSidePanelTab(saved === "preview" && !onLoadOutputEditor ? "customization" : saved);
  }, [changeSidePanelTab, editor.id, onLoadOutputEditor]);
  const handleViewTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, currentView: EditorView) => {
    const currentIndex = editorViewOrder.indexOf(currentView);
    const nextView = event.key === "Home"
      ? editorViewOrder[0]
      : event.key === "End"
        ? editorViewOrder[editorViewOrder.length - 1]
        : event.key === "ArrowRight"
          ? editorViewOrder[(currentIndex + 1) % editorViewOrder.length]
          : event.key === "ArrowLeft"
            ? editorViewOrder[(currentIndex - 1 + editorViewOrder.length) % editorViewOrder.length]
            : null;
    if (!nextView) return;
    event.preventDefault();
    if (selectEditorView(nextView)) {
      document.getElementById(`itinerary-view-tab-${nextView}`)?.focus();
    }
  };

  const openOutput = useCallback((trigger: EventTarget | null) => {
    if (!canPreview || !(trigger instanceof HTMLElement)) return;
    outputTriggerRef.current = trigger;
    restoreOutputFocusRef.current = false;
    setOutputOpen(true);
  }, [canPreview]);

  useEffect(() => {
    setPendingSchoolReturnSave(undefined);
  }, [editor.id]);

  const [fallbackGenerating, setFallbackGenerating] = useState(false);
  const fallbackInFlight = useRef(false);
  const generateDraftContent = useCallback(async () => {
    if (!onEnrich || onRewritePendingSummaries || fallbackInFlight.current || !editingEnabled) return;
    fallbackInFlight.current = true;
    setFallbackGenerating(true);
    setSaveSummaryWarning("");
    try {
      const pending = pendingContentSlots(editorRef.current);
      for (const [dayId, slots] of Object.entries(pending)) {
        for (const slot of slots) {
          try {
            await composeDaySummary({ dayId, slot, automatic: true,
              getEditor: () => editorRef.current,
              onChange: (next) => { editorRef.current = next; onChange(next); },
              enrich: onEnrich, completePointDescriptions: onCompletePointDescriptions,
            });
          } catch {
            setSaveSummaryWarning("行程介绍还没补写完成，这次没有保存。请再试一次，也可以到“内容编辑”自己补写。");
          }
        }
      }
    } finally {
      fallbackInFlight.current = false;
      setFallbackGenerating(false);
    }
  }, [editingEnabled, onChange, onCompletePointDescriptions, onEnrich, onRewritePendingSummaries]);
  const saveBlockedByContent = contentSaveBlocked || summaryRewriting || fallbackGenerating
    || Boolean(onEnrich && !onRewritePendingSummaries && Object.keys(pendingContentSlots(editor)).length);
  const contentGenerating = summaryRewriting || fallbackGenerating;

  const handleManualSave = useCallback(async (trigger?: EventTarget | null, retry = false, confirmed = false) => {
    if (manualSavePendingRef.current || !editingEnabled || saveStatus === "saving" || contentGenerating) return;
    if (!confirmed && requiresSchoolReturnConfirmation(editorRef.current)) {
      setPendingSchoolReturnSave({ editorId: editorRef.current.id, trigger, retry });
      return;
    }
    manualSavePendingRef.current = true;
    setManualSavePending(true);
    setSaveSummaryWarning("");
    try {
      if (retry) {
        await onRetrySave();
        return;
      }
      const saved = await onSave();
      if (saved === false) return;
      const hasSavedSidePreview = saved === true && hasCustomizationPanel && Boolean(onLoadOutputEditor);
      // 只有完整草稿保存成功后，右栏才重新读取数据库快照。
      if (hasSavedSidePreview) {
        setSidePreviewRefreshSequence((value) => value + 1);
        changeSidePanelTab("preview");
      }
      if (saved === true && !hasSavedSidePreview) openOutput(trigger ?? document.activeElement);
    } finally {
      manualSavePendingRef.current = false;
      setManualSavePending(false);
    }
  }, [changeSidePanelTab, editingEnabled, hasCustomizationPanel, onLoadOutputEditor, onRetrySave, onSave, openOutput, saveStatus, contentGenerating]);

  useEffect(() => {
    setModalDraftDirty(false);
  }, [onLocalDraftChange, selectedDayId]);

  useEffect(() => {
    onLocalDraftChange?.(modalDraftDirty || mainDataDirty);
  }, [modalDraftDirty, mainDataDirty, onLocalDraftChange]);

  useEffect(() => {
    if (!globalThis.matchMedia) return;
    const media = globalThis.matchMedia("(max-width: 768px)");
    const update = () => {
      setDaysOverlayMode(media.matches);
      if (!media.matches) {
        setDaysOpen(false);
        setDaysSidebarCollapsed(false);
      }
    };
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!globalThis.matchMedia) return;
    const media = globalThis.matchMedia("(max-width: 1024px)");
    const update = () => {
      setStackedPanels(media.matches);
      setCustomizationPanelOpen(true);
      setCustomizationPanelSize(undefined);
      customizationPanelSizeRef.current = undefined;
      expandedCustomizationPanelSizeRef.current = undefined;
    };
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  const openDays = (trigger: EventTarget | null) => {
    if (!(trigger instanceof HTMLElement)) return;
    daysTriggerRef.current = trigger;
    restoreDaysFocusRef.current = false;
    setDaysOpen(true);
  };
  const closeDays = () => {
    restoreDaysFocusRef.current = true;
    setDaysOpen(false);
  };
  const restoreDaysFocus = () => {
    if (!restoreDaysFocusRef.current) return;
    restoreDaysFocusRef.current = false;
    daysTriggerRef.current?.focus();
  };
  const closeOutput = () => {
    restoreOutputFocusRef.current = true;
    setOutputOpen(false);
  };
  const restoreOutputFocus = () => {
    if (!restoreOutputFocusRef.current) return;
    restoreOutputFocusRef.current = false;
    outputTriggerRef.current?.focus();
  };
  const daySidebar = (
    <ItineraryDaySidebar
      editor={editor}
      selectedDayId={selectedDay?.id ?? ""}
      readOnly={!editingEnabled}
      onChange={onChange}
      onSelectDay={(dayId) => {
        onLocalDraftChange?.(false);
        onSelectDay(dayId);
        closeDays();
      }}
      onAnnounce={setAnnouncement}
      listPointCandidates={onListPointCandidates}
    />
  );
  const handleRetrySave = () => void handleManualSave(undefined, true);
  const issueDetails = validationIssues.map((issue) => {
    const key = JSON.stringify([issue.code, issue.subjectKey ?? null, issue.detail]);
    const dayId = resolveValidationSubject(editor, issue.subjectKey)?.day.id
      ?? editor.days.find((day) => day.id === issue.subjectKey
        || day.nodes.some((node) => node.id === issue.subjectKey))?.id
      ?? editor.segments.find((segment) => segment.id === issue.subjectKey)?.startDayId;
    return { key, detail: formatValidationIssue(issue, editor), dayId };
  });
  const stayDetails = editor.days.flatMap((day) => {
    const detail = dayStayNoticeDetail(day);
    return detail ? [{ key: `day-stay-mismatch:${day.id}`, detail, dayId: day.id }] : [];
  });
  const orderedDayEndpoints = [...editor.days]
    .sort((left, right) => left.dayNumber - right.dayNumber)
    .map((day) => ({ day, endpoints: summarizeDayEndpoints(resolveDayEndpointNodes(day)) }))
    .filter(({ endpoints }) => endpoints !== null);
  const stayContinuityDetails = orderedDayEndpoints.flatMap(({ day, endpoints }, index) => {
    const previous = orderedDayEndpoints[index - 1];
    const from = previous?.endpoints?.last;
    const to = endpoints?.first;
    if (from?.nodeType !== "stay_area" || to?.nodeType !== "stay_area"
      || summarizeDayEndpoints([from, to])?.isSameLocation) return [];
    return [{
      key: `stay-continuity:${previous.day.id}:${day.id}`,
      dayId: day.id,
      detail: `第 ${previous.day.dayNumber} 天最后住在“${from.title}”，第 ${day.dayNumber} 天却从“${to.title}”出发。请确认中间是否需要换地方；可以先保存，之后再调整。`,
    }];
  });
  const validationDetails = [...new Map(
    issueDetails.map((item) => [item.key, item]),
  ).values()];
  const stayNoticeDetails = [...new Map(
    [...stayDetails, ...stayContinuityDetails].map((item) => [item.key, item]),
  ).values()];
  const noticeCount = Number(repairActive)
    + Number(Boolean(associationNotice))
    + Number(Boolean(legacySyncNotice))
    + Number(!canUpdate)
    + Number(canUpdate && editor.status === "confirmed")
    + Number(Boolean(errorMessage))
    + Number(Boolean(saveSummaryWarning))
    + Number(Boolean(summaryRewriteWarning))
    + validationDetails.length
    + stayNoticeDetails.length;
  const noticeDayAction = (dayId?: string) => {
    const targetDay = editor.days.find((day) => day.id === dayId);
    return targetDay ? <Button type="text" size="mini" onClick={() => {
      onSelectDay(targetDay.id);
      selectEditorView("skeleton");
    }}>查看第 {targetDay.dayNumber} 天</Button> : null;
  };
  const hasNotices = noticeCount > 0;
  useEffect(() => {
    if (!hasNotices || announcementsState !== "initial") return undefined;
    const timeout = window.setTimeout(() => setAnnouncementsState("collapsed"), 3000);
    return () => window.clearTimeout(timeout);
  }, [editor.id, hasNotices, announcementsState]);
  useEffect(() => {
    // 包含本地校验失败的手动保存周期；同次失败的重渲染保留人工收起选择。
    if (errorMessage && displayedSaveStatus === "error") setAnnouncementsState("expanded");
  }, [editor.id, errorMessage, displayedSaveStatus]);
  useEffect(() => {
    if (summaryRewriteWarning && !summaryRewriting) setAnnouncementsState("expanded");
  }, [editor.id, summaryRewriteWarning, summaryRewriting]);
  const daysPane = (
    <aside ref={daysSidebarRef} className={`itinerary-desktop-days${daysSidebarCollapsed ? " is-collapsed" : ""}`}>
      {daysSidebarCollapsed ? (
        <>
          <nav className="itinerary-day-compact-rail" aria-label="快速切换行程日期">
            {editor.days.map((day) => (
              <button
                type="button"
                key={day.id}
                className={day.id === selectedDay?.id ? "is-active" : undefined}
                aria-current={day.id === selectedDay?.id ? "date" : undefined}
                aria-label={"D" + day.dayNumber + " 第" + day.dayNumber + "天"}
                title={"第" + day.dayNumber + "天" + (day.dayTitle?.trim() ? "：" + day.dayTitle : "：待定")}
                onClick={() => {
                  onLocalDraftChange?.(false);
                  onSelectDay(day.id);
                }}
              >
                D{day.dayNumber}
              </button>
            ))}
          </nav>
        </>
      ) : <>
        <div className="itinerary-day-sidebar-scroll">{daySidebar}</div>
      </>}
    </aside>
  );
  const canvasPane = (
    <section className="itinerary-editor-canvas">
      <div className="itinerary-canvas-toolbar">
      {canUpdate ? <div className="itinerary-view-switch">
        <div className="itinerary-view-tabs" role="tablist" aria-label="行程编辑视图">
          <button
            id="itinerary-view-tab-skeleton"
            type="button"
            role="tab"
            aria-selected={view === "skeleton"}
            aria-controls="itinerary-view-panel-skeleton"
            tabIndex={view === "skeleton" ? 0 : -1}
            onClick={() => selectEditorView("skeleton")}
            onKeyDown={(event) => handleViewTabKeyDown(event, "skeleton")}
          >
            <span className="itinerary-view-step" aria-hidden="true">1</span>骨架编辑
          </button>
          <button
            id="itinerary-view-tab-content"
            type="button"
            role="tab"
            aria-selected={view === "content"}
            aria-controls="itinerary-view-panel-content"
            tabIndex={view === "content" ? 0 : -1}
            onClick={() => selectEditorView("content")}
            onKeyDown={(event) => handleViewTabKeyDown(event, "content")}
          >
            <span className="itinerary-view-step" aria-hidden="true">2</span>内容编辑
          </button>
          <button
            id="itinerary-view-tab-services"
            type="button"
            role="tab"
            aria-selected={view === "services"}
            aria-controls="itinerary-view-panel-services"
            tabIndex={view === "services" ? 0 : -1}
            onClick={() => selectEditorView("services")}
            onKeyDown={(event) => handleViewTabKeyDown(event, "services")}
          >
            <span className="itinerary-view-step" aria-hidden="true">3</span>全程服务标准
          </button>
        </div>
        <div className="itinerary-view-actions" role="group" aria-label="客户输出">
          {canPreview ? <button type="button" onClick={(event) => openOutput(event.currentTarget)}><span className="itinerary-view-step" aria-hidden="true">4</span>客户预览</button> : null}
        </div>
      </div> : null}
      </div>
      {!canUpdate ? (canPreview ? (
        <ItineraryClientDetail
          itinerary={derivePublicItinerary(editor)}
          initialDayNumber={selectedDay?.dayNumber}
          internalActions={enableInternalInteractions ? <ItineraryInternalActions
            itineraryId={editor.id}
            enabled={!canUpdate}
          /> : undefined}
        />
      ) : <Alert type="info" content="没有行程预览权限，请联系管理员授权。" />) : selectedDay || view === "services" ? (
        <div
          id={`itinerary-view-panel-${view}`}
          className={`itinerary-editor-view-panel${editorDisabled ? " is-disabled" : ""}`}
          role="tabpanel"
          aria-labelledby={`itinerary-view-tab-${view}`}
          aria-disabled={editorDisabled || undefined}
        >
          <fieldset className="itinerary-editor-disabled-fieldset" disabled={editorDisabled}>
            {view === "services" ? (
              <ItineraryServiceStandards
                value={editor.customerServiceStandards}
                customerNotices={editor.customerNotices}
                onNoticesChange={(customerNotices) => onChange({ ...editor, customerNotices })}
                onChange={(customerServiceStandards) => onChange({ ...editor, customerServiceStandards })}
                onTicketFocus={() => setQuickPreviewItineraryId(editor.id)}
              />
            ) : selectedDay && (view === "skeleton" ? (
              <ItinerarySkeletonPanel
                editor={editor}
                day={selectedDay}
                onChange={onChange}
                onAnnounce={setAnnouncement}
                listPointCandidates={onListPointCandidates}
                listMajorCandidates={onListMajorCandidates}
                getRailwayOptions={onGetRailwayOptions}
                getFlightOptions={onGetFlightOptions}
                getDrivingRoute={onGetDrivingRoute}
                getWalkingRoute={onGetWalkingRoute}
                calculatingSegmentIds={calculatingSegmentIds}
                onModalDraftChange={setModalDraftDirty}
              />
            ) : (
              <ItineraryContentPanel
                editor={editor}
                day={selectedDay}
                onChange={onChange}
                autoGenerateEmptyContent={false}
                summaryRewriting={summaryRewriting}
                autoGenerateEmptySummaries={!onRewritePendingSummaries}
                onCompletePointDescriptions={onCompletePointDescriptions}
                onRewriteSummary={onEnrich ? (slot, signal, snapshot) => requestDaySummaryRewrite(snapshot ?? editorRef.current, selectedDay.id, slot, onEnrich, signal) : undefined}
              />
            ))}
          </fieldset>
        </div>
      ) : (
        <div
          id={`itinerary-view-panel-${view}`}
          className="itinerary-editor-empty"
          role="tabpanel"
          aria-labelledby={`itinerary-view-tab-${view}`}
        >
          请先添加行程天数
        </div>
      )}
    </section>
  );
  const allowedSidePanelTab = (sidePanelTab === "preview" && (!canPreview || !onLoadOutputEditor))
    || (sidePanelTab === "table" && !canViewTable) ? "customization" : sidePanelTab;
  const sidePanelContents: Record<SidePanelTab, ReactNode> = {
    customization: customizationPanel,
    table: canViewTable ? <ItineraryTablePanel editor={editor} /> : null,
    preview: canPreview && onLoadOutputEditor ? <ItinerarySidePreview
      itineraryId={editor.id} savedRevision={savedRevision} loadEditor={onLoadOutputEditor}
      refreshSequence={sidePreviewRefreshSequence}
    /> : null,
  };
  const customizationPane = (
    <aside
      className={`itinerary-customization-panel-slot${auxiliaryPanelOpen ? "" : " is-collapsed"}`}
      aria-label="行程编辑辅助信息"
    >
      {quickPreviewOpen ? <ItineraryQuickPreview editor={editor} onClose={closeQuickPreview} /> : hasCustomizationPanel && customizationPanelOpen ? (
        <div className="itinerary-customization-panel-content">
          <Tabs activeTab={allowedSidePanelTab} onChange={(key) => changeSidePanelTab(key as SidePanelTab)}
            justify destroyOnHide animation={false} size="small" style={{ height: "100%" }}>
            {ITINERARY_SIDE_TABS.map((tab) => <Tabs.TabPane key={tab.key} title={tab.title} disabled={(tab.key === "preview" && (!canPreview || !onLoadOutputEditor)) || (tab.key === "table" && !canViewTable)}>
              <div className="itinerary-side-tab-content">
                <p className="itinerary-side-tab-description">{tab.description}</p>
                <div className={`itinerary-customization-panel-body${tab.key === "customization" ? "" : " is-preview"}`}>
                  {sidePanelContents[tab.key]}
                </div>
              </div>
            </Tabs.TabPane>)}
          </Tabs>
        </div>
      ) : null}
    </aside>
  );
  const editorPane = daysOverlayMode ? canvasPane : (
    <ResizeBox.SplitGroup
      key={editor.id}
      ref={daysSplitRef}
      className="itinerary-days-split"
      style={{ width: "100%", height: "100%" }}
      onMovingStart={() => { daysPanelInteractedRef.current = true; }}
      onMoving={() => { daysPanelInteractedRef.current = true; }}
      panes={[
        { content: daysPane, size: daysPanelSize, min: 0.05, max: 0.3, collapsible: { prev: true } },
        { content: canvasPane, min: 0.3 },
      ]}
    />
  );

  return (
    <main className={`itinerary-editor-page${embed ? " is-embed" : ""}`} aria-busy={reloadPending || undefined}>
      <header className="itinerary-editor-toolbar">
        <div className="itinerary-editor-title">
          <nav className="itinerary-editor-breadcrumbs" aria-label="面包屑">
            课程线路管理 / 行程设计
          </nav>
          <div className="itinerary-editor-title-line">
            {!embed && onExit ? (
              <Button
                className="itinerary-editor-back"
                type="text"
                icon={<ArrowLeft size={18} />}
                aria-label="返回课程线路列表"
                title="返回课程线路列表"
                onClick={onExit}
              />
            ) : null}
            <div className="itinerary-editor-heading-copy">
              <h1 title={editor.title || "未命名行程"}>{editor.title || "未命名行程"}</h1>
              <span className="itinerary-editor-status-pill">
                {itineraryStatusLabels[editor.status] ?? editor.status}
              </span>
            </div>
          </div>
        </div>
        <div className="itinerary-editor-commands" aria-label="行程操作">
          {editingEnabled && onCreateMainData && (canCreatePoint || canCreateStayArea) && (
            <div className="itinerary-main-data-actions">
              {canCreatePoint && <Button onClick={() => setMainDataKind("point")}>添加考察点</Button>}
              {canCreateStayArea && <Button onClick={() => setMainDataKind("stay_area")}>添加住宿点</Button>}
            </div>
          )}
          <div className={`itinerary-save-state is-${visibleSaveStatus.className}`} aria-label={`保存状态：${visibleSaveStatus.label}`}>
            <StatusIcon size={16} className={displayedSaveStatus === "saving" ? "is-spinning" : ""} />
            <span>{visibleSaveStatus.label}</span>
          </div>
          <div className="itinerary-toolbar-actions">
            {reviewActions}
            <Button className="itinerary-mobile-days-button" icon={<CalendarDays size={16} />} onClick={(event) => openDays(event.currentTarget)}>
              天数
            </Button>
            {editingEnabled && saveStatus === "error" ? (
              <Button icon={<RefreshCw size={16} />} onClick={handleRetrySave}>
                重试保存
              </Button>
            ) : null}
            {editingEnabled ? (
              <Button
                icon={<RefreshCw size={16} />}
                aria-label="重新获取数据库数据"
                title="重新获取数据库数据"
                disabled={displayedSaveStatus === "saving"}
                onClick={() => setPendingReloadLatest(true)}
              >
                重新获取
              </Button>
            ) : null}
            {editingEnabled ? <Button
              type="primary"
              icon={<Save size={16} />}
              loading={displayedSaveStatus === "saving"}
              disabled={displayedSaveStatus === "saving" || contentGenerating}
              title="保存当前编辑进度"
              onClick={(event) => void handleManualSave(event.currentTarget)}
            >
              保存
            </Button> : null}
          </div>
        </div>
      </header>
      {!outputOpen && outputNotice ? <Alert
        type={outputNotice.type}
        content={<span>{outputNotice.text} {outputNotice.action}</span>}
      /> : null}
      {saveBlockedByContent ? <div role="status" className="itinerary-auto-ai-status">
        {summaryRewriting || fallbackGenerating ? <><Spin size={14} /> 正在自动补写地点介绍和每天的行程介绍，请稍等</> : <>
          有些行程介绍还需要更新。打开“内容编辑”或点击“保存”时会自动补写，你自己写的内容会保留。
          <Button size="small" disabled={manualSavePending} onClick={() => void (onRewritePendingSummaries ? onRewritePendingSummaries() : generateDraftContent())}>重试文案生成</Button>
        </>}
      </div> : null}

      <div className="itinerary-editor-workspace">
        <div className={`itinerary-editor-notices${announcementsCollapsed && noticeCount ? " is-collapsed" : ""}`}>
          {noticeCount ? (
            <button
              type="button"
              className="itinerary-editor-notices-summary"
              aria-label={announcementsCollapsed ? `展开${noticeCount}条提示` : "收起提示"}
              aria-expanded={!announcementsCollapsed}
              onClick={() => setAnnouncementsState(announcementsCollapsed ? "expanded" : "collapsed")}
            >
              <span>当前有 {noticeCount} 条提示，点击{announcementsCollapsed ? "展开查看" : "收起提示"}</span>
              {announcementsCollapsed ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronUp size={14} aria-hidden="true" />}
            </button>
          ) : null}
          {!announcementsCollapsed ? <>
          {associationNotice}
          {legacySyncNotice}
          {saveSummaryWarning ? <Alert className="itinerary-editor-alert" type="warning" content={saveSummaryWarning} /> : null}
          {summaryRewriteWarning ? <Alert className="itinerary-editor-alert" type="warning" content={summaryRewriteWarning} /> : null}
          {repairActive ? (
            <Alert
              className="itinerary-editor-alert"
              type="warning"
              content={`${repairRequiredCount > 0 ? `有 ${repairRequiredCount} 段旧交通找不到明确的出发地或目的地，暂时无法显示。` : ""}还有 ${repairRemainingCount} 段交通需要确认，可以先保存，之后再补充。`}
            />
          ) : null}
          {!canUpdate ? <Alert type="info" content="当前账号只能查看行程。需要修改时，请联系管理员开通编辑权限。" /> : null}
          {canUpdate && editor.status === "confirmed" ? (
            <Alert
              type="info"
              content="当前行程已定稿，如需修改，请先在客户预览中取消定稿。"
            />
          ) : null}
          {errorMessage ? (
            <div role="alert">
              <Alert
                className="itinerary-editor-alert"
                type="error"
                content={<div className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                  {errorMessage}
                  {errorDiagnostic ? <details className="mt-2">
                    <summary className="cursor-pointer">查看错误详情（可发给管理员）</summary>
                    <div>{errorDiagnostic}</div>
                  </details> : null}
                </div>}
              />
            </div>
          ) : null}
          {validationDetails.length ? (
            <Alert
              className="itinerary-editor-alert"
              type="warning"
              content={(
                <div className="itinerary-validation-summary">
                  <strong>
                    当前有 {validationDetails.length} 项待完善，
                    {editingEnabled
                      ? "仍可继续编辑和保存。"
                      : editor.status === "confirmed"
                        ? "取消定稿后可继续完善。"
                        : "当前为只读状态。"}
                  </strong>
                  <ul>
                    {(allValidationVisible ? validationDetails : validationDetails.slice(0, 3)).map((item) => (
                      <li key={item.key}>{item.detail}{noticeDayAction(item.dayId)}</li>
                    ))}
                  </ul>
                  {validationDetails.length > 3
                    ? <Button type="text" size="mini" aria-expanded={allValidationVisible}
                        onClick={() => setAllValidationVisible((visible) => !visible)}>
                        {allValidationVisible ? "收起待完善清单" : `查看全部 ${validationDetails.length} 项待完善`}
                      </Button>
                    : null}
                </div>
              )}
            />
          ) : null}
          {stayNoticeDetails.length ? (
            <Alert
              className="itinerary-editor-alert"
              type="info"
              content={(
                <div className="itinerary-validation-summary">
                  <strong>有几处住宿需要你确认，可以先保存，之后再调整。</strong>
                  <ul>
                    {(allStayNoticesVisible ? stayNoticeDetails : stayNoticeDetails.slice(0, 3)).map((item) => (
                      <li key={item.key}>{item.detail}{noticeDayAction(item.dayId)}</li>
                    ))}
                  </ul>
                  {stayNoticeDetails.length > 3
                    ? <Button type="text" size="mini" aria-expanded={allStayNoticesVisible}
                        onClick={() => setAllStayNoticesVisible((visible) => !visible)}>
                        {allStayNoticesVisible ? "收起住宿核对清单" : `查看全部 ${stayNoticeDetails.length} 项住宿核对`}
                      </Button>
                    : null}
                </div>
              )}
            />
          ) : null}
          </> : null}
        </div>

        <div className={`itinerary-editor-layout${canUpdate ? "" : " is-readonly"}${
          hasAuxiliaryPanel ? " has-customization-panel" : ""
        }${hasAuxiliaryPanel && !auxiliaryPanelOpen ? " is-customization-panel-collapsed" : ""}${daysSidebarCollapsed ? " is-days-sidebar-collapsed" : ""}`}>
          {hasCustomizationPanel || canUpdate ? (
            <ResizeBox.SplitGroup
              key={editor.id}
              className="itinerary-workspace-split"
              style={{ width: "100%", height: "100%" }}
              direction={stackedPanels ? "vertical" : "horizontal"}
              onMoving={(event, sizes) => {
                if (quickPreviewOpen) return;
                const open = Number.parseFloat(sizes[1]) > 3;
                const size = restoredCollapsedPanel && open && event.type === "click"
                  ? expandedCustomizationPanelSizeRef.current ?? defaultAuxiliaryPanelSize : sizes[1];
                setCustomizationPanelOpen(open);
                customizationPanelSizeRef.current = size;
                if (open) {
                  expandedCustomizationPanelSizeRef.current = size;
                  if (restoredCollapsedPanel) setCustomizationPanelSize(size);
                }
              }}
              panes={[
                { content: editorPane, size: hasAuxiliaryPanel && !restoredCollapsedPanel ? undefined : 1, min: stackedPanels ? 0.4 : 0.35,
                  disabled: !hasAuxiliaryPanel, collapsible: { next: hasCustomizationPanel && !quickPreviewOpen } },
                { content: customizationPane,
                  size: quickPreviewOpen ? 0.4
                    : hasCustomizationPanel ? customizationPanelSize ?? defaultAuxiliaryPanelSize : 0,
                  min: quickPreviewOpen ? 0.2 : 0, max: 0.5 },
              ]}
            />
          ) : editorPane}

        </div>
      </div>

      <div className={`itinerary-mobile-actions${editingEnabled ? "" : " is-readonly"}`} aria-label="移动端主要操作">
        <Button icon={<CalendarDays size={18} />} onClick={(event) => openDays(event.currentTarget)}>天数</Button>
        {editingEnabled && saveStatus === "error" ? (
          <Button icon={<RefreshCw size={18} />} onClick={handleRetrySave}>
            重试保存
          </Button>
        ) : editingEnabled ? (
          <Button
            type="primary"
            icon={<Save size={18} />}
            loading={displayedSaveStatus === "saving"}
            disabled={displayedSaveStatus === "saving" || contentGenerating}
            title="保存当前编辑进度"
            onClick={(event) => void handleManualSave(event.currentTarget)}
          >
            保存
          </Button>
        ) : null}
      </div>

      {mainDataKind && onCreateMainData && <ItineraryMainDataDialog
        key={`${editor.id}:${mainDataKind}`}
        kind={mainDataKind}
        defaultStayArea={selectedDay?.stayAreaId && selectedDay.stayAreaName
          ? { id: selectedDay.stayAreaId, name: selectedDay.stayAreaName } : undefined}
        onCreate={onCreateMainData}
        onListCandidates={onListPointCandidates}
        onDirtyChange={setMainDataDirty}
        disabled={!editingEnabled || (mainDataKind === "point" ? !canCreatePoint : !canCreateStayArea)}
        onClose={() => setMainDataKind(undefined)}
      />}
      <Drawer
        title="行程日期"
        closeIcon={<ItineraryDialogCloseButton label="关闭行程日期" />}
        visible={daysOverlayMode && daysOpen}
        width={320}
        footer={null}
        onCancel={closeDays}
        afterClose={restoreDaysFocus}
      >
        {daysOverlayMode && daysOpen ? daySidebar : null}
      </Drawer>
      <ItineraryOutputDrawer
        visible={canPreview && outputOpen}
        editor={editor}
        initialDayId={selectedDay?.id}
        initialDayNumber={selectedDay?.dayNumber}
        loadEditor={onLoadOutputEditor}
        canViewTable={canViewTable}
        canExport={canExport}
        canShare={canShare}
        copyActions={copyActions}
        moveActions={moveActions}
        onStartMove={onStartMove}
        onCancelMove={onCancelMove}
        movePending={movePending}
        onClose={closeOutput}
        afterClose={restoreOutputFocus}
        lifecycleActions={lifecycleActions}
        notice={outputNotice}
        enableInternalInteractions={enableInternalInteractions}
      />
      <Modal
        visible={pendingSchoolReturnSave?.editorId === editor.id}
        title="确认保存行程"
        closeIcon={<ItineraryDialogCloseButton label="关闭确认保存行程" />}
        okText="仍然保存"
        cancelText="取消"
        okButtonProps={{ disabled: !editingEnabled || displayedSaveStatus === "saving" || contentGenerating }}
        onOk={() => {
          const pending = pendingSchoolReturnSave;
          setPendingSchoolReturnSave(undefined);
          if (pending?.editorId === editorRef.current.id) {
            void handleManualSave(pending.trigger, pending.retry, true);
          }
        }}
        onCancel={() => setPendingSchoolReturnSave(undefined)}
      >
        本行程从学校校区出发，但最后一天结束地点不是学校校区，是否仍要保存？
      </Modal>
      <Modal
        visible={pendingReloadLatest}
        title="重新获取数据库数据"
        closeIcon={<ItineraryDialogCloseButton label="关闭重新获取数据库数据" />}
        okText="确认重新获取"
        cancelText="取消"
        okButtonProps={{ disabled: displayedSaveStatus === "saving" }}
        onOk={async () => {
          if (displayedSaveStatus === "saving" || editorDisabled) return;
          setPendingReloadLatest(false);
          setReloadPending(true);
          try {
            await onReloadLatest?.();
          } finally {
            setReloadPending(false);
          }
        }}
        onCancel={() => setPendingReloadLatest(false)}
      >
        重新获取后将丢弃当前前端草稿，并恢复服务端已保存状态，是否继续？
      </Modal>
      <div className="sr-only" role="status" aria-live="polite">{announcement}</div>
      <div className="sr-only" aria-label="保存状态播报" aria-live="polite">
        {visibleSaveStatus.label}
      </div>
    </main>
  );
}

/** 行程编辑器加载状态。 */
export function ItineraryEditorLoading() {
  return (
    <div className="itinerary-editor-loading" role="status" aria-live="polite">
      <Spin size={32} />
      <span>正在加载行程...</span>
    </div>
  );
}
